import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useSchedules } from '../../lib/api/hooks-tms.js';
import { Empty, ErrorBanner, TableSkeleton, CardHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * The days the account is shut.
 *
 * Not a new mechanism: each holiday becomes a dated rule above the weekly one, which schedules and
 * the switch already understand (0092). What this page is for is entering 16 December once instead
 * of on every schedule, and filling in next year's calendar without typing twelve dates.
 *
 * A holiday closes the whole day unless hours are given, which is how an office that works a half
 * day on Christmas Eve says so.
 */

interface Holiday {
  id: string; name: string;
  recurrence: 'once' | 'annual' | 'easter';
  on_date: string | null; month: number | null; day: number | null;
  easter_offset: number | null; observed_monday: boolean;
  time_from: string | null; time_to: string | null;
  schedule_id: string | null; schedule_name: string | null;
}
/** A day a holiday lands on in the year being looked at; the server works these out. */
interface Day {
  holiday_id: string; date: string; name: string; observed: boolean;
  time_from: string | null; time_to: string | null; schedule_name: string | null;
}
interface Payload { data: Holiday[]; days: Day[]; year: number }

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const readable = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getUTCDay()];
  return `${day} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

const isPast = (iso: string): boolean => iso < new Date().toISOString().slice(0, 10);

/** How a holiday repeats, in the words somebody would use. */
function repeats(h: Holiday): string {
  if (h.recurrence === 'annual') return 'Every year';
  if (h.recurrence === 'easter') {
    return h.easter_offset === -2 ? 'Easter, moves each year'
      : h.easter_offset === 1 ? 'Easter, moves each year'
        : `${Math.abs(h.easter_offset ?? 0)} days ${(h.easter_offset ?? 0) < 0 ? 'before' : 'after'} Easter`;
  }
  return 'Once';
}

export function HolidaysTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const writable = can('extension:write');
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [adding, setAdding] = useState(false);

  const key = ['tenants', tenantId, 'holidays', year] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<Payload>(`/v1/tenants/${tenantId}/holidays?year=${year}`),
  });
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'holidays'] });
  };

  const fill = useMutation({
    mutationFn: () => api.post<{ added: number; already_there: number }>(
      `/v1/tenants/${tenantId}/holidays/actions/import`, { calendar: 'ZA' }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/holidays/${id}`),
    onSuccess: invalidate,
  });

  if (q.error) return <ErrorBanner error={q.error} />;

  const rules = q.data?.data ?? [];
  const days = q.data?.days ?? [];
  const years = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];
  const ahead = days.filter((d) => !isPast(d.date)).length;
  const byId = new Map(rules.map((r) => [r.id, r]));

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Public holidays</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Days the account is closed. A holiday beats the opening hours underneath it, on every
            schedule unless one is named. Yearly holidays carry forward on their own, and the ones
            that follow Easter move with it.
          </p>
        </div>
      </div>

      <div className="arc-toolbar" style={{ marginBottom: 14 }}>
        <div className="arc-row" style={{ gap: 4, flexWrap: 'wrap' }} role="group" aria-label="Year">
          {years.map((y) => (
            <button key={y} type="button"
              className={`arc-btn arc-btn-sm ${y === year ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
              onClick={() => setYear(y)}>
              {y}
            </button>
          ))}
        </div>
        <div className="arc-spacer" />
        {writable && (
          <>
            <button type="button" className="arc-btn arc-btn-sm" disabled={fill.isPending}
              title="Adds the twelve South African public holidays, for this year and every year after it, leaving any you have already entered alone"
              onClick={() => fill.mutate()}>
              {fill.isPending ? 'Adding…' : 'Add South African public holidays'}
            </button>
            <button disabled={adding} type="button" className="arc-btn arc-btn-primary arc-btn-sm"
              onClick={() => setAdding((v) => !v)}>
              <Icon name="plus" size={13} /> New holiday
            </button>
          </>
        )}
      </div>

      {fill.error && <ErrorBanner error={fill.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}
      {fill.isSuccess && !fill.isPending && (
        <p className="arc-subtle" style={{ fontSize: 12.5, marginTop: -6, marginBottom: 12 }}>
          Added {fill.data.added}
          {fill.data.already_there > 0 && `, ${fill.data.already_there} were already listed`}.
        </p>
      )}

      {adding && (
        <NewHoliday tenantId={tenantId} year={year}
          onDone={() => { setAdding(false); invalidate(); }} />
      )}

      <div className="arc-card">
        {q.data === undefined ? <TableSkeleton rows={5} cols={6} /> : days.length === 0 ? (
          <Empty icon="clock" title={`No holidays listed for ${year}`}
            hint={writable
              ? 'Add the South African public holidays, or a day of your own. Without them, calls follow the ordinary opening hours.'
              : 'Calls follow the ordinary opening hours.'} />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th style={{ width: 210 }}>Day</th>
                  <th>Holiday</th>
                  <th style={{ width: 160 }}>Repeats</th>
                  <th style={{ width: 170 }}>Calls</th>
                  <th style={{ width: 150 }}>Applies to</th>
                  {writable && <th style={{ width: 80 }} />}
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const rule = byId.get(d.holiday_id);
                  return (
                    <tr key={`${d.holiday_id}-${d.date}`}
                      style={isPast(d.date) ? { opacity: 0.55 } : undefined}>
                      <td>
                        <span className="arc-mono" style={{ fontSize: 12.5 }}>{d.date}</span>
                        <div className="arc-subtle" style={{ fontSize: 11.5 }}>{readable(d.date)}</div>
                      </td>
                      <td style={{ fontWeight: 550 }}>{d.name}</td>
                      <td className="arc-muted" style={{ fontSize: 12.5 }}>
                        {d.observed ? 'Stands in for the Sunday' : rule ? repeats(rule) : ''}
                      </td>
                      <td>
                        {d.time_from
                          ? <span>Open {d.time_from}–{d.time_to}</span>
                          : <span>Closed all day</span>}
                      </td>
                      <td className="arc-muted arc-truncate">
                        {d.schedule_name ?? 'Every schedule'}
                      </td>
                      {writable && (
                        <td className="arc-td-right">
                          {/* The substituted Monday is made by the rule above it, not stored. */}
                          {!d.observed && (
                            <RowAction kind="delete" label={`Remove ${d.name}`}
                              disabled={remove.isPending}
                              onClick={() => {
                                const forever = rule && rule.recurrence !== 'once';
                                void confirmAction({
                                  title: forever ? `Remove ${rule?.name} from every year?` : `Remove ${d.name} on ${d.date}?`,
                                  body: forever ? 'Calls follow the ordinary hours on that day again.' : undefined,
                                  action: 'Remove', danger: true,
                                }).then((ok) => { if (ok) remove.mutate(d.holiday_id); });
                              }} />
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {days.length > 0 && (
        <p className="arc-subtle" style={{ fontSize: 12, marginTop: 10 }}>
          {ahead === 0
            ? `Every holiday in ${year} has passed. ${rules.filter((r) => r.recurrence !== 'once').length} carry on into next year.`
            : `${ahead} still to come in ${year}.`}
        </p>
      )}
    </>
  );
}

/** One day: the date, what it is called, and whether the phones answer at all. */
function NewHoliday({ tenantId, year, onDone }: {
  tenantId: string; year: number; onDone: () => void;
}) {
  const [date, setDate] = useState(`${year}-01-01`);
  const [name, setName] = useState('');
  // Yearly by default: a holiday that has to be entered again next year is one that gets missed.
  const [yearly, setYearly] = useState(true);
  const [closed, setClosed] = useState(true);
  const [from, setFrom] = useState('08:00');
  const [to, setTo] = useState('13:00');
  const [scheduleId, setScheduleId] = useState('');
  const schedules = useSchedules(tenantId);

  const save = useMutation({
    mutationFn: () => api.post(`/v1/tenants/${tenantId}/holidays`, {
      name: name.trim(),
      ...(yearly
        ? { recurrence: 'annual', month: Number(date.slice(5, 7)), day: Number(date.slice(8, 10)),
            observed_monday: true }
        : { recurrence: 'once', on_date: date }),
      ...(closed ? {} : { time_from: from, time_to: to }),
      ...(scheduleId ? { schedule_id: scheduleId } : {}),
    }),
    onSuccess: onDone,
  });

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Add a day" />
      <div className="arc-card-body">
        <div className="arc-row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="h-date">Date</label>
            <input id="h-date" className="arc-input" type="date" value={date}
              onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
            <label className="arc-label" htmlFor="h-name">What it is</label>
            <input id="h-name" className="arc-input" value={name} placeholder="Company shutdown"
              onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="arc-field" style={{ margin: 0, minWidth: 200 }}>
            <label className="arc-label" htmlFor="h-sched">Applies to</label>
            <select id="h-sched" className="arc-select" value={scheduleId}
              onChange={(e) => setScheduleId(e.target.value)}>
              <option value="">Every schedule</option>
              {(schedules.data?.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="arc-row" style={{ gap: 4, marginTop: 14, flexWrap: 'wrap' }}
          role="group" aria-label="How often">
          <button type="button" className={`arc-btn arc-btn-sm ${yearly ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
            onClick={() => setYearly(true)}>Every year</button>
          <button type="button" className={`arc-btn arc-btn-sm ${yearly ? 'arc-btn-ghost' : 'arc-btn-primary'}`}
            onClick={() => setYearly(false)}>This date only</button>
          <span className="arc-subtle" style={{ fontSize: 12, marginLeft: 8, alignSelf: 'center' }}>
            {yearly
              ? 'Returns on the same date every year, and closes the Monday when it falls on a Sunday.'
              : 'A one-off, such as a shutdown.'}
          </span>
        </div>

        <div className="arc-row" style={{ gap: 4, marginTop: 14, flexWrap: 'wrap' }}
          role="group" aria-label="Calls that day">
          <button type="button" className={`arc-btn arc-btn-sm ${closed ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
            onClick={() => setClosed(true)}>Closed all day</button>
          <button type="button" className={`arc-btn arc-btn-sm ${closed ? 'arc-btn-ghost' : 'arc-btn-primary'}`}
            onClick={() => setClosed(false)}>Open for part of it</button>
          {!closed && (
            <span className="arc-row" style={{ gap: 6, marginLeft: 8 }}>
              <input className="arc-input" type="time" value={from} aria-label="Opens"
                style={{ width: 120 }} onChange={(e) => setFrom(e.target.value)} />
              <span className="arc-muted">to</span>
              <input className="arc-input" type="time" value={to} aria-label="Closes"
                style={{ width: 120 }} onChange={(e) => setTo(e.target.value)} />
            </span>
          )}
        </div>

        {save.error && <ErrorBanner error={save.error} />}

        <div className="arc-row" style={{ gap: 8, marginTop: 14 }}>
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={!name.trim() || save.isPending || (!closed && to <= from)}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Adding…' : 'Add day'}
          </button>
          <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          {!closed && to <= from && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>It has to close after it opens.</span>
          )}
        </div>
      </div>
    </div>
  );
}
